import { Trans, useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { CustomDialog } from "@medoma/components/Dialog/Dialog";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Text } from "@medoma/components/Text/Text";
import { humanFormat } from "@medoma/models/date-and-time";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
  type IExistingPatient,
} from "@medoma/models/patients";
import { daysAgo } from "@/Utils/DateUtils";
import styles from "./DischargeReview.module.scss";

export const DischargeReview = ({
  patient,
  dischargeDate,
  activitiesToCloseCount,
  activitiesToKeepCount,
  isDischarging,
  errorMessage,
  onCancel,
  onDischarge,
}: {
  patient: IExistingPatient;
  dischargeDate: Date;
  activitiesToCloseCount: number;
  activitiesToKeepCount: number;
  isDischarging: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onDischarge: () => void;
}) => {
  const { t } = useLingui();

  return (
    <CustomDialog
      isOpen={true}
      onClose={onCancel}
      title={t`Granska utskrivning`}
      actions={
        <>
          <OutlinedButton onClick={onCancel}>{t`Avbryt`}</OutlinedButton>
          <FilledButton disabled={isDischarging} onClick={onDischarge}>
            {t`Skriv ut`}
          </FilledButton>
        </>
      }
    >
      <div className={styles.review}>
        <dl className={styles.summary}>
          <dt>
            <Trans>Patient</Trans>
          </dt>
          <dd>{getPatientFullName(patient.name)}</dd>
          <dt>
            <Trans>Personnummer</Trans>
          </dt>
          <dd>
            {formatPersonalIdentityNumber({
              personalIdentityNumber: patient.personalIdentityNumber,
              personalIdentityNumberType: patient.personalIdentityNumberType,
            })}
          </dd>
          <dt>
            <Trans>Utskrivningsdatum</Trans>
          </dt>
          <dd>
            <Text element="span" weight="medium">
              {humanFormat(dischargeDate, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </Text>{" "}
            <Text element="span" color="faded">
              ({daysAgo(dischargeDate)})
            </Text>
          </dd>
          {activitiesToCloseCount > 0 || activitiesToKeepCount > 0 ? (
            <>
              <dt>
                <Trans>Aktiviteter</Trans>
              </dt>
              <dd>
                <ul className={styles.consequences}>
                  {activitiesToCloseCount > 0 ? (
                    <li>
                      {plural(activitiesToCloseCount, {
                        one: "# aktivitet avslutas",
                        other: "# aktiviteter avslutas",
                      })}
                    </li>
                  ) : null}
                  {activitiesToKeepCount > 0 ? (
                    <li>
                      {plural(activitiesToKeepCount, {
                        one: "# aktivitet fortsätter",
                        other: "# aktiviteter fortsätter",
                      })}
                    </li>
                  ) : null}
                </ul>
              </dd>
            </>
          ) : null}
        </dl>
        <Text element="p">
          <Trans>
            <Text element="span" weight="bold">
              Utskrivningen går inte att ångra.
            </Text>{" "}
            Vårdperioden avslutas. Om patienten återregistreras skapas en ny
            vårdperiod.
          </Trans>
        </Text>
        {errorMessage ? <ErrorMessage message={errorMessage} /> : null}
      </div>
    </CustomDialog>
  );
};
